import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { stompService } from 'services/socketService';
import { downloadAssetsFile, startDownloadAssets } from '../functions/download';
import { startUploadAssets } from '../functions/Upload';

/* ----------------------------------
   HELPERS
---------------------------------- */

const createTempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/* ----------------------------------
   DOWNLOAD PROGRESS CONFIG
---------------------------------- */

export const DOWNLOAD_PROGRESS = {
  PREPARING: { start: 0, end: 10 },
  PROCESSING: { start: 10, end: 20 },
  DOWNLOADING: { start: 20, end: 99 },
  DONE: { start: 99, end: 100 },
};

export const UPLOAD_PROGRESS = {
  PREPARING: { start: 0, end: 10 },
  UPLOADING: { start: 10, end: 85 },
  PROCESSING: { start: 85, end: 95 },
  DONE: { start: 95, end: 100 },
};

const DownloadAssetsContext = createContext(null);

/* ----------------------------------
   GENERIC PROGRESS MAPPER
---------------------------------- */

const mapProgress = (phase, percent = 0, states = DOWNLOAD_PROGRESS) => {
  const cfg = states[phase];
  if (!cfg) return 0;

  if (cfg.start === cfg.end) return cfg.end;

  return Math.min(cfg.end, cfg.start + Math.floor(((cfg.end - cfg.start) * percent) / 100));
};

/* ==================================
   PROVIDER
================================== */

export const DownloadAssetsProvider = ({ children }) => {
  const [downloadingAssets, setDownloadingAssets] = useState([]);
  const [globalDownloadProgress, setGlobalDownloadProgress] = useState(0);
  const [refreshRef, setrefreshRef] = useState(0);

  const uploadListeners = useRef([]);
  const uploadIdMapRef = useRef({});
  const uploadMetaRef = useRef({});

  /* ----------------------------------
   SOCKET SUBSCRIPTION
---------------------------------- */

  useEffect(() => {
    const unsubscribe = stompService.subscribe('/user/topic/media.dam.final', (data) => {
      uploadListeners.current.forEach((cb) =>
        cb({
          ...data,
          activityId: data.activityId || data.uniqueId,
        }),
      );

      handleDownloadSocket(data);
      handleUploadSocket(data);
    });

    return () => unsubscribe?.();
  }, []);

  /* ----------------------------------
   DOWNLOAD SOCKET HANDLER
---------------------------------- */

  const handleDownloadSocket = async (data) => {
    const { uniqueId, status } = data;
    if (!uniqueId) return;

    if (status === 'PREPARING') {
      updateStatus(uniqueId, 'PREPARING', mapProgress('PREPARING', 100));
      return;
    }

    if (status === 'PROCESSING') {
      updateStatus(uniqueId, 'PROCESSING', mapProgress('PROCESSING', 100));
      return;
    }

    if (status === 'READY') {
      updateStatus(uniqueId, 'DOWNLOADING', mapProgress('DOWNLOADING', 0));

      notify(uniqueId, 'DOWNLOADING', mapProgress('DOWNLOADING', 0), data);

      try {
        await downloadAssetsFile({ downloadId: uniqueId }, (downloadPercent) => {
          const progress = mapProgress('DOWNLOADING', downloadPercent);

          updateStatus(uniqueId, 'DOWNLOADING', progress);
          notify(uniqueId, 'DOWNLOADING', progress, data);
        });

        updateStatus(uniqueId, 'DONE', DOWNLOAD_PROGRESS.DONE.end);

        notify(uniqueId, 2, DOWNLOAD_PROGRESS.DONE.end, data);

        cleanup(uniqueId);
      } catch {
        updateStatus(uniqueId, 'FAILED', 0);
        notify(uniqueId, 'FAILED', 0, data);
        cleanup(uniqueId);
      }

      return;
    }

    if (status === 'FAILED') {
      updateStatus(uniqueId, 'FAILED', 0);
      notify(uniqueId, 'FAILED', 0, data);
      cleanup(uniqueId);
    }
  };

  /* ----------------------------------
   START DOWNLOAD
---------------------------------- */

  const startDownload = async (payload) => {
    const job = await startDownloadAssets(payload);

    notify(job.downloadId, 'PREPARING', 0, {});

    setDownloadingAssets((prev) => [
      ...prev,
      {
        id: job.downloadId,
        payload,
        fileName: job.fileName,
        status: 'PREPARING',
        progress: mapProgress('PREPARING', 0),
        showProgress: true,
      },
    ]);
  };

  /* ----------------------------------
   STATE HELPERS
---------------------------------- */

  const updateStatus = (id, status, progress = 0) => {
    setDownloadingAssets((prev) => {
      const updated = prev.map((d) =>
        d.id === id ? { ...d, status, progress, showProgress: true } : d,
      );

      const active = updated.find((d) => d.status === 'DOWNLOADING');

      setGlobalDownloadProgress(active ? active.progress : 0);

      return updated;
    });
  };

  const cleanup = (id) => {
    setDownloadingAssets((prev) => {
      const updated = prev.filter((d) => d.id !== id);

      const active = updated.find((d) => d.status === 'DOWNLOADING');

      setGlobalDownloadProgress(active ? active.progress : 0);

      return updated;
    });
  };

  /* ----------------------------------
   LISTENER NOTIFIER
---------------------------------- */

  const notify = (id, status, progress, data = {}) => {
    const meta = uploadMetaRef.current[id] || {};

    uploadListeners.current.forEach((cb) => {
      cb({
        ...data,
        activityId: data.activityId || id,
        status,
        progress,
        totalNumberOfAsset: meta.totalNumberOfAsset,
        successCount: meta.successCount || 0,
        showProgress: true,
      });
    });
  };

  /* ----------------------------------
   REGISTER LISTENER
---------------------------------- */

  const registerUploadListener = (cb) => {
    uploadListeners.current.push(cb);

    return () => {
      uploadListeners.current = uploadListeners.current.filter((fn) => fn !== cb);
    };
  };

  /* ----------------------------------
   START UPLOAD
---------------------------------- */

  const UploadFiles = async (formData, totalfiles) => {
    const tempId = createTempId();

    uploadMetaRef.current[tempId] = {
      totalNumberOfAsset: totalfiles,
      successCount: 0,
      lastCalculatedUploaded: 0,
    };

    notify(tempId, 'PREPARING', 0, {
      uploadId: tempId,
      isTemp: true,
    });

    try {
      const job = await startUploadAssets(formData, (percent) => {
        const mapped = mapProgress('UPLOADING', percent, UPLOAD_PROGRESS);

        const meta = uploadMetaRef.current[tempId];
        if (!meta) return;

        /* calculate uploaded files */

        const uploadedFiles = Math.floor((percent / 100) * meta.totalNumberOfAsset);

        if (uploadedFiles > meta.lastCalculatedUploaded) {
          const diff = uploadedFiles - meta.lastCalculatedUploaded;

          meta.successCount += diff;
          meta.lastCalculatedUploaded = uploadedFiles;
        }

        notify(tempId, 'UPLOADING', mapped);
      });

      uploadIdMapRef.current[job.uploadId] = tempId;

      uploadMetaRef.current[job.uploadId] = uploadMetaRef.current[tempId];

      notify(tempId, 'SWAP', 0, {
        action: 'SWAP',
        oldId: tempId,
        newId: job.uploadId,
      });

      return job;
    } catch (err) {
      notify(tempId, 'FAILED', 0);

      delete uploadMetaRef.current[tempId];

      throw err;
    }
  };

  /* ----------------------------------
   HANDLE UPLOAD SOCKET
---------------------------------- */

  const handleUploadSocket = (data) => {
    const { activityId: requestId, status, percent = 0 } = data;

    if (!requestId) return;

    const mappedId = requestId;

    data = {
      ...data,
      showProgress: true,
      progress: mapProgress(status, percent, UPLOAD_PROGRESS),
    };

    if (status === 1) {
      notify(mappedId, 'PROCESSING', mapProgress('PROCESSING', 100, UPLOAD_PROGRESS), data);

      return;
    }

    if (status === 2) {
      const meta = uploadMetaRef.current[requestId];
      uploadMetaRef.current[requestId] = {
        ...uploadMetaRef.current[requestId],
        Responsecount: uploadMetaRef.current[requestId]?.Responsecount
          ? uploadMetaRef.current[requestId].Responsecount + 1
          : 1,
      };

      const remaining = meta.Responsecount ?  meta.Responsecount + 1 : 1;

      if (meta.totalNumberOfAsset && remaining >= meta.totalNumberOfAsset) {
        notify(mappedId, 2, UPLOAD_PROGRESS.DONE.end, data);

        delete uploadMetaRef.current[requestId];
        delete uploadIdMapRef.current[requestId];

        setrefreshRef((p) => p + 1);
      }

      return;
    }

    if (status === 'FAILED') {
      notify(mappedId, 'FAILED', 0, data);

      delete uploadMetaRef.current[requestId];
      delete uploadIdMapRef.current[requestId];
    }
  };

  /* ----------------------------------
   PROVIDER VALUE
---------------------------------- */

  return (
    <DownloadAssetsContext.Provider
      value={{
        startDownload,
        downloadingAssets,
        registerUploadListener,
        globalDownloadProgress,
        DOWNLOAD_PROGRESS,
        UploadFiles,
        UPLOAD_PROGRESS,
        refreshRef,
      }}
    >
      {children}
    </DownloadAssetsContext.Provider>
  );
};

/* ----------------------------------
   HOOK
---------------------------------- */

export const useDownloadAssets = () => {
  const ctx = useContext(DownloadAssetsContext);

  if (!ctx) {
    throw new Error('useDownloadAssets must be used inside DownloadAssetsProvider');
  }

  return ctx;
};
